import { createSingleEventSaga, MyAction } from '@mrnkr/redux-saga-toolbox';
import { createActions, createReducer } from 'reduxsauce';
import { SagaIterator } from 'redux-saga';
import { put, take, select } from 'redux-saga/effects';
import { Action } from 'redux';

import { ProviderFile } from '../typings';
import { ArgsWithHeaders } from '../utils/typings';
import { putAuthInfoInArgs } from './auth.module';
import { noOpAction } from '../utils/noOpAction';
import { AUTH_API_URL } from '../config';
import { Creators as LoadingActions } from './loading.module';
import { Creators as ErrorActions, Types as ErrorTypes } from './errors.module';
import { MMDError } from '../utils/MMDError';
import { MyState } from '../store';

interface ActionTypes {
  REQUEST_PROVIDER_FILES: string;
  COMMIT_PROVIDER_FILES: string;
  REQUEST_UPLOAD_PROVIDER_FILE: string;
  COMMIT_UPLOAD_PROVIDER_FILE: string;
  REQUEST_DELETE_PROVIDER_FILE: string;
  COMMIT_DELETE_PROVIDER_FILE: string;
  SET_PROVIDER_FILES_LOADING: string;
  SET_PROVIDER_FILES_NOT_LOADING: string;
}

interface ProviderFilesResponse {
  count: number;
  data: ProviderFile[];
}

export interface UploadProviderFilePayload {
  categoryId: number;
  fileName: string;
}

export interface DeleteProviderFilePayload {
  fileId: number;
}

interface ActionCreators {
  requestProviderFiles: () => MyAction<ProviderFilesResponse>;
  commitProviderFiles: (payload: ProviderFile[]) => MyAction<ProviderFile[]>;
  requestUploadProviderFile: (
    payload: UploadProviderFilePayload,
  ) => MyAction<UploadProviderFilePayload>;
  commitUploadProviderFile: (payload: any) => MyAction<any>;
  requestDeleteProviderFile: (
    payload: DeleteProviderFilePayload,
  ) => MyAction<DeleteProviderFilePayload>;
  commitDeleteProviderFile: (payload: any) => MyAction<any>;
  setProviderFilesLoading: () => MyAction<void>;
  setProviderFilesNotLoading: () => MyAction<void>;
}

export const { Creators, Types } = createActions<ActionTypes, ActionCreators>({
  requestProviderFiles: [],
  commitProviderFiles: ['payload'],
  requestUploadProviderFile: ['payload'],
  commitUploadProviderFile: ['payload'],
  requestDeleteProviderFile: ['payload'],
  commitDeleteProviderFile: ['payload'],
  setProviderFilesLoading: [],
  setProviderFilesNotLoading: [],
});

// State slice and reducer for Provider Files
export type ProviderFilesState = {
  files: ProviderFile[];
  loading: boolean;
};

function extractDisplayName(fileName: string): string {
  const parts = fileName.split('/');
  return parts[parts.length - 1];
}

function commitProviderFilesReducer(
  state: ProviderFilesState,
  action: MyAction<ProviderFile[]>,
): ProviderFilesState {
  const filesWithDisplayNames = (action.payload || []).map((fileCategory) => ({
    ...fileCategory,
    files: fileCategory.files.map((file) => ({
      ...file,
      displayName: extractDisplayName(file.name),
    })),
  }));

  return {
    ...state,
    files: filesWithDisplayNames,
    loading: false,
  };
}

function commitUploadProviderFile(
  state: ProviderFilesState,
): ProviderFilesState {
  return {
    ...state,
    loading: false,
  };
}

function commitDeleteProviderFile(
  state: ProviderFilesState,
): ProviderFilesState {
  return {
    ...state,
    loading: false,
  };
}

function setProviderFilesLoading(
  state: ProviderFilesState,
): ProviderFilesState {
  return {
    ...state,
    loading: true,
  };
}

function setProviderFilesNotLoading(
  state: ProviderFilesState,
): ProviderFilesState {
  return {
    ...state,
    loading: false,
  };
}

const initialState: ProviderFilesState = {
  files: [],
  loading: false,
};

export const providerFilesReducer = createReducer<ProviderFilesState>(
  initialState,
  {
    [Types.COMMIT_PROVIDER_FILES]: commitProviderFilesReducer,
    [Types.COMMIT_UPLOAD_PROVIDER_FILE]: commitUploadProviderFile,
    [Types.COMMIT_DELETE_PROVIDER_FILE]: commitDeleteProviderFile,
    [Types.SET_PROVIDER_FILES_LOADING]: setProviderFilesLoading,
    [Types.SET_PROVIDER_FILES_NOT_LOADING]: setProviderFilesNotLoading,
  },
);

async function downloadProviderFiles({
  headers,
}: ArgsWithHeaders<object>): Promise<ProviderFile[]> {
  const result = await fetch(`${AUTH_API_URL}/doctors/files`, {
    headers,
    method: 'GET',
  });

  if (!result.ok) {
    throw new MMDError('An error has occurred when fetching provider files');
  }

  const response = await result.json();

  return response.data;
}

async function uploadProviderFile({
  headers,
  ...payload
}: ArgsWithHeaders<UploadProviderFilePayload>): Promise<any> {
  const result = await fetch(`${AUTH_API_URL}/doctors/files`, {
    headers,
    method: 'POST',
    body: JSON.stringify({
      categoryId: payload.categoryId,
      fileName: payload.fileName,
    }),
  });

  if (!result.ok) {
    throw new MMDError('An error has occurred uploading provider file');
  }

  return await result.json();
}

async function deleteProviderFile({
  headers,
  ...payload
}: ArgsWithHeaders<DeleteProviderFilePayload>): Promise<void> {
  const result = await fetch(
    `${AUTH_API_URL}/doctors/files/${payload.fileId}`,
    {
      headers,
      method: 'DELETE',
    },
  );

  if (!result.ok) {
    throw new MMDError('An error has occurred deleting provider file');
  }
}

const requestProviderFilesWatcher = createSingleEventSaga<
  object,
  ProviderFile[],
  MyAction<object>
>({
  takeEvery: Types.REQUEST_PROVIDER_FILES,
  loadingAction: LoadingActions.setLoading,
  commitAction: Creators.commitProviderFiles,
  successAction: noOpAction,
  errorAction: ErrorActions.setError,
  action: downloadProviderFiles,
  beforeAction: putAuthInfoInArgs,
});

const requestUploadProviderFileWatcher = createSingleEventSaga<
  UploadProviderFilePayload,
  any,
  MyAction<UploadProviderFilePayload>
>({
  takeEvery: Types.REQUEST_UPLOAD_PROVIDER_FILE,
  loadingAction: LoadingActions.setLoading,
  commitAction: Creators.commitUploadProviderFile,
  successAction: noOpAction,
  errorAction: ErrorActions.setError,
  action: uploadProviderFile,
  beforeAction: putAuthInfoInArgs,
  // @ts-ignore

  *afterAction(res: any): SagaIterator {
    // Refetch files after successful upload
    yield put(Creators.requestProviderFiles());
  },
});

const requestDeleteProviderFileWatcher = createSingleEventSaga<
  DeleteProviderFilePayload,
  void,
  MyAction<DeleteProviderFilePayload>
>({
  takeEvery: Types.REQUEST_DELETE_PROVIDER_FILE,
  loadingAction: LoadingActions.setLoading,
  commitAction: Creators.commitDeleteProviderFile,
  successAction: noOpAction,
  errorAction: ErrorActions.setError,
  action: deleteProviderFile,
  beforeAction: putAuthInfoInArgs,
  // @ts-ignore

  *afterAction(): SagaIterator {
    // Refetch files after successful deletion
    yield put(Creators.requestProviderFiles());
  },
});

// Saga to clear loading state on errors
function* clearLoadingOnError(): SagaIterator {
  while (yield take(ErrorTypes.SET_ERROR)) {
    // Only clear loading if we're currently in a loading state
    const providerFilesState: ProviderFilesState = yield select(
      (state: MyState) => state.providerFiles,
    );
    if (providerFilesState.loading) {
      yield put(Creators.setProviderFilesNotLoading());
    }
  }
}

export const providerFilesSagas = [
  requestProviderFilesWatcher,
  requestUploadProviderFileWatcher,
  requestDeleteProviderFileWatcher,
  clearLoadingOnError,
];

export const selectProviderFilesState = (state: MyState) =>
  state.providerFiles.files;
export const selectProviderFilesLoading = (state: MyState) =>
  state.providerFiles.loading;
